import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ZegoUIKitPrebuilt } from "@zegocloud/zego-uikit-prebuilt";
import { Spinner, Button } from "react-bootstrap";
import axios from "axios";
import { getURLbyEndPointV2 } from "../../../store/api";

const PublicCloudMeetingRoom = () => {
  const { meetingRoomId } = useParams();
  const [loading, setLoading] = useState(true);
  const [meetingValid, setMeetingValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // This state monitors the recording status for the UI buttons
  const [isRecording, setIsRecording] = useState(false); // Can be 'RECORDING', 'PAUSED', 'STOPPED' or boolean
  const [meetingData, setMeetingData] = useState(null);

  // Waiting Room state
  const [joinRequests, setJoinRequests] = useState([]);
  const [showWaitlist, setShowWaitlist] = useState(true);
  // Hardcoded credentials
  const appID = 1871154113;
  const serverSecret = "7625a483cfcaa01920c9f3ceeaab8ff2";

  // Validate meeting exists & get initial status
  const checkMeeting = React.useCallback(async () => {
    try {
      const url = getURLbyEndPointV2("getCloudMeeting", meetingRoomId);
      const res = await axios.get(url + "?role=admin");
      if (res.data.status) {
        setMeetingValid(true);
        setMeetingData(res.data.data);
        if (res.data.data.recordingStatus === "RECORDING")
          setIsRecording("RECORDING");
        else if (res.data.data.recordingStatus === "PAUSED")
          setIsRecording("PAUSED");
        else setIsRecording(false);
      } else {
        setMeetingValid(false);
        setErrorMessage("Meeting not found or invalid.");
      }
    } catch (error) {
      console.error("Meeting check failed", error);
      setMeetingValid(false);
      setErrorMessage(
        error.response?.data?.message ||
          "An error occurred while validating the meeting.",
      );
    } finally {
      setLoading(false);
    }
  }, [meetingRoomId]);

  const fetchJoinRequests = React.useCallback(async () => {
    try {
      const res = await axios.get(
        getURLbyEndPointV2("getJoinRequests") + meetingRoomId,
      );
      if (res.data.status) {
        setJoinRequests(res.data.requests);
      }
    } catch (error) {
      console.error("Failed to fetch join requests", error);
    }
  }, [meetingRoomId]);

  useEffect(() => {
    checkMeeting();
    fetchJoinRequests();

    const intervalId = setInterval(() => {
      fetchJoinRequests();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [checkMeeting, fetchJoinRequests]);

  const handleApprove = async (userId) => {
    try {
      const res = await axios.post(getURLbyEndPointV2("approveJoinRequest"), {
        meetingRoomId,
        userId,
      });
      if (res.data.status) {
        fetchJoinRequests();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleReject = async (userId) => {
    try {
      const res = await axios.post(getURLbyEndPointV2("rejectJoinRequest"), {
        meetingRoomId,
        userId,
      });
      if (res.data.status) {
        fetchJoinRequests();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const toggleRecording = async (action) => {
    try {
      if (action === "start") {
        if (!window.confirm("Start Cloud Recording?")) return;
        await axios.post(getURLbyEndPointV2("startEventRecording"), {
          meetingRoomId,
        });
        setIsRecording("RECORDING");
        alert("Recording Started");
      } else if (action === "pause") {
        await axios.post(getURLbyEndPointV2("pauseEventRecording"), {
          meetingRoomId,
        });
        setIsRecording("PAUSED");
        alert("Recording Paused");
      } else if (action === "resume") {
        await axios.post(getURLbyEndPointV2("resumeEventRecording"), {
          meetingRoomId,
        });
        setIsRecording("RECORDING");
        alert("Recording Resumed");
      } else if (action === "stop") {
        if (!window.confirm("Stop Cloud Recording?")) return;
        await axios.post(getURLbyEndPointV2("stopEventRecording"), {
          meetingRoomId,
        });
        setIsRecording(false);
        alert("Recording Stopped");
      } else if (action === "snapshot") {
        await axios.post(getURLbyEndPointV2("takeEventSnapshot"), {
          meetingRoomId,
        });
        alert("Snapshot Taken");
      }
      checkMeeting();
    } catch (error) {
      console.error(`Failed to ${action} recording`, error);
      alert(`Failed to ${action} recording`);
    }
  };

  const zpRef = React.useRef(false);

  const myMeeting = async (element) => {
    if (!element || zpRef.current) return;
    zpRef.current = true;

    // Default to Administrator if joining from master
    const userID = `admin_${Math.floor(Math.random() * 1000)}`;
    const userName = "AECCI ADMINISTRATOR";

    try {
      // Use generateKitTokenForTest for immediate reliability.
      // Ensure appID and serverSecret match your Zego dashboard.
      const kitToken = ZegoUIKitPrebuilt.generateKitTokenForTest(
        appID,
        serverSecret,
        meetingRoomId,
        userID,
        userName,
      );

      // const getDynamicHeader = () => {
      //   if (!meetingData) return "AECCI-IAC";
      //   let formattedDate = "";
      //   if (meetingData.meetingDate) {
      //     const d = new Date(meetingData.meetingDate);
      //     const DD = String(d.getDate()).padStart(2, "0");
      //     const MM = String(d.getMonth() + 1).padStart(2, "0");
      //     const YYYY = d.getFullYear();
      //     formattedDate = `${DD}/${MM}/${YYYY}`;
      //   }
      //   let formattedTime = meetingData.meetingTime || "";
      //   if (formattedTime) {
      //     const [hours, minutes] = formattedTime.split(":");
      //     const hoursInt = parseInt(hours, 10);
      //     const ampm = hoursInt >= 12 ? "PM" : "AM";
      //     const displayHours = hoursInt % 12 || 12;
      //     formattedTime = `${String(displayHours).padStart(2, "0")}:${minutes}${ampm}`;
      //   }
      //   return `AECCI-IAC, ${meetingData.meetingTitle || "Meeting"}, ${formattedDate} ${formattedTime}`.trim();
      // };

      const zp = ZegoUIKitPrebuilt.create(kitToken);

      zp.joinRoom({
        container: element,
        scenario: {
          mode: ZegoUIKitPrebuilt.VideoConference,
        },
        showPreJoinView: true,
        branding: {
          logoURL:
            "https://www.aecci.org.in/wp-content/uploads/2020/10/AECCI-24.png",
          // headerTitle: getDynamicHeader(),
        },
        layout: "Grid",
        showScreenSharingButton: true,
        showMyCameraToggleButton: true,
        showMyMicrophoneToggleButton: true,
        turnOnCameraWhenJoining: true,
        turnOnMicrophoneWhenJoining: true,
        showUserList: true,
        showTextChat: true,
        showLeavingView: true,
        showLeaveRoomConfirmDialog: true,
        showRemoveUserButton: true,
        sharedLinks: [
          {
            name: "Join as participant",
            url: `https://e-platform.aecci.org.in/meeting-room/${meetingRoomId}`,
          },
          {
            name: "Join as speaker",
            url: `https://e-platform.aecci.org.in/speaker-join/${meetingRoomId}`,
          },
        ],
        onLeaveRoom: async () => {
          try {
            await axios.post(getURLbyEndPointV2("stopEventRecording"), {
              meetingRoomId,
            });
          } catch (e) {
            console.error("Auto-stop failed", e);
          }
          window.location.href = "/";
        },
      });
    } catch (error) {
      console.error("Meeting setup failed", error);
    }
  };

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#0B1A33",
        position: "relative",
      }}
    >
      {meetingValid && !loading && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "20px",
            zIndex: 10000,
            display: "flex",
            gap: "10px",
            backgroundColor: "rgba(0,0,0,0.5)",
            padding: "10px",
            borderRadius: "8px",
          }}
        >
          {!isRecording ? (
            <Button
              variant="success"
              size="sm"
              onClick={() => toggleRecording("start")}
            >
              Start Recording
            </Button>
          ) : isRecording === "PAUSED" ? (
            <>
              <Button
                variant="success"
                size="sm"
                onClick={() => toggleRecording("resume")}
              >
                Resume
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => toggleRecording("stop")}
              >
                Stop
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={() => toggleRecording("snapshot")}
              >
                Snapshot
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="warning"
                size="sm"
                onClick={() => toggleRecording("pause")}
              >
                Pause
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => toggleRecording("stop")}
              >
                Stop
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={() => toggleRecording("snapshot")}
              >
                Snapshot
              </Button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center align-items-center h-100 text-white">
          <Spinner animation="border" />{" "}
          <span className="ms-2">Validating Meeting...</span>
        </div>
      ) : meetingValid ? (
        <>
          <style>{`
            .custom-meeting-banner {
              position: absolute;
              top: 10px;
              left: 50%;
              transform: translateX(-50%);
              padding: 10px 20px;
              background: #19649D;
              color: white;
              font-size: 18px;
              font-weight: 600;
              border-radius: 8px;
              z-index: 9999;
              text-align: center;
              white-space: nowrap;
            }
            @media (max-width: 768px) {
              .custom-meeting-banner {
                width: 100vw;
                max-width: 100vw;
                top: 0;
                border-radius: 0;
                font-size: 13px;
                padding: 8px 10px;
                white-space: normal;
              }
            }
          `}</style>
          <div className="custom-meeting-banner">
            {meetingData
              ? (() => {
                  let formattedDate = "";
                  if (meetingData.meetingDate) {
                    const d = new Date(meetingData.meetingDate);
                    const DD = String(d.getDate()).padStart(2, "0");
                    const MM = String(d.getMonth() + 1).padStart(2, "0");
                    const YYYY = d.getFullYear();
                    formattedDate = `${DD}/${MM}/${YYYY}`;
                  }
                  let formattedTime = meetingData.meetingTime || "";
                  if (formattedTime) {
                    const [hours, minutes] = formattedTime.split(":");
                    const hoursInt = parseInt(hours, 10);
                    const ampm = hoursInt >= 12 ? "PM" : "AM";
                    const displayHours = hoursInt % 12 || 12;
                    formattedTime = `${String(displayHours).padStart(2, "0")}:${minutes}${ampm}`;
                  }
                  return `AECCI-IAC, ${meetingData.meetingTitle || "Meeting"}`.trim();
                })()
              : "AECCI-IAC"}
          </div>
          <div
            ref={myMeeting}
            style={{ width: "100vw", height: "100vh" }}
          ></div>
        </>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center h-100 text-white flex-column p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", borderRadius: "10px" }}
        >
          <h3>Access Denied / Invalid Meeting</h3>
          <p>
            {errorMessage || "This meeting may have expired or does not exist."}
          </p>
        </div>
      )}

      {/* Waiting Room Panel */}
      {meetingValid && (
        <div
          style={{
            position: "absolute",
            top: "130px",
            right: "20px",
            width: "320px",
            height: "calc(100vh - 150px)",
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 10000,
            display: showWaitlist ? "flex" : "none",
            flexDirection: "column",
            overflow: "hidden",
            transition: "all 0.3s ease",
          }}
        >
          <div
            style={{
              padding: "15px",
              borderBottom: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#f8f9fa",
            }}
          >
            <h5 className="m-0 text-dark">
              Waiting Room ({joinRequests.length})
            </h5>
            <Button
              variant="light"
              size="sm"
              onClick={() => setShowWaitlist(false)}
            >
              Close
            </Button>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "10px",
            }}
          >
            {joinRequests.length === 0 ? (
              <p className="text-muted text-center mt-3">No pending requests</p>
            ) : (
              joinRequests.map((req, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: "10px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    marginBottom: "10px",
                    backgroundColor: "#fefefe",
                  }}
                >
                  <strong className="d-block text-dark">{req.name}</strong>
                  <small className="text-muted d-block mb-2">
                    {req.role} {req.email ? `- ${req.email}` : ""}
                  </small>
                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      className="flex-fill"
                      onClick={() => handleApprove(req.userId)}
                    >
                      Admit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      className="flex-fill"
                      onClick={() => handleReject(req.userId)}
                    >
                      Deny
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {meetingValid && !showWaitlist && (
        <Button
          variant="primary"
          style={{
            position: "absolute",
            top: "130px",
            right: "20px",
            zIndex: 10000,
            borderRadius: "50px",
          }}
          onClick={() => setShowWaitlist(true)}
        >
          Waiting Room ({joinRequests.length})
        </Button>
      )}
    </div>
  );
};

export default PublicCloudMeetingRoom;
